<template>
    <div>
        <b-modal
            no-close-on-esc
            no-close-on-backdrop
            size="xl"
            hide-footer
            v-model="showModal"
            :title="$t('product.importHeader')"
            scrollable
        >
            <b-form
                ref="nedbrowserForm"
                class="col-xs-12 col-lg-9 pb-5"
                name="nedbrowserForm"
                v-on:submit.prevent="$_onSubmit"
            >
                <b-form-group
                    id="fieldset-1"
                    :description="description"
                    :label="$t('product.selectImportFile')"
                    :label-for="name"
                    :label-class="labelClass"
                    :label-size="labelSize"
                >
                    <div :id="`input_${name}`" class="import-group">
                        <div class="import-control" v-on:click="$_selectFile">
                            <em class="fas fa-plus-circle import-icon"></em>
                            <span
                                :id="`selected_${name}`"
                                class="import-name"
                                >{{ fileName }}</span
                            >
                        </div>
                        <!-- Invisible file selector -->
                        <input
                            v-bind="$attrs"
                            ref="fileSelector"
                            v-show="false"
                            type="file"
                            accept=".json"
                            :id="name"
                            :name="name"
                            :value="value"
                            @change="$_onChange"
                            :data-fieldname="name"
                        />
                    </div>
                    <div v-if="importData">
                        <div class="col-xs-8 col-md-5">
                            <x-input
                                :text="$t('product.importProduct')"
                                id="name"
                                v-model="importData.productName"
                                readonly
                            >
                            </x-input>
                        </div>
                        <div class="col-xs-8 col-md-5">
                            <x-input
                                :text="$t('product.version')"
                                id="version"
                                v-model="importData.productVersion"
                                readonly
                            >
                            </x-input>
                        </div>

                        <div class="col-xs-8 col-md-5">
                            <b-button
                                type="submit"
                                id="save_button"
                                variant="primary"
                                class="d-flex align-items-center"
                            >
                                {{ $t('product.import') }}&nbsp;<span
                                    class="fas"
                                    v-bind:class="{
                                        'fa-check': !isSaving,
                                        'fa-sync-alt fa-spin': isSaving
                                    }"
                                ></span>
                            </b-button>
                        </div>
                    </div>
                </b-form-group>
            </b-form>
        </b-modal>
    </div>
</template>

<script>
    import { nprogress } from '../../services/utilities';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import XInput from '../../components/BaseComponents/x-input.vue';

    export default {
        name: 'NedBrowserImport',

        mixins: [modalMixin],

        components: {
            'x-input': XInput
        },

        props: {
            description: { type: String, required: false },
            name: { type: String, required: false, default: 'nedbrowser-import' },
            labelSize: { type: String, required: false, default: '' },
            value: [String, Number],
            labelClass: { type: String, default: 'font-weight-normal' },
            launchModal: { type: Boolean, required: false, default: false }
        },

        data() {
            return {
                showModal: false,
                isSaving: false,
                isDeleting: false,
                importData: null,
                fileName: 'Geen bestand gekozen'
            };
        },

        async mounted() {},

        methods: {
            async $_showModal() {
                this.showModal = true;
            },
            $_hideModal() {
                this.showModal = false;
            },
            /**
             * File selected event handler
             * @param  {object} event [holds the selected files]
             */
            $_onChange(event) {
                const files = event.currentTarget.files;
                if (files.length > 0) {
                    this.fileName = files[0].name;
                    // read file content
                    this.readFileContent(files).then(
                        (data) => {
                            this.importData = data;
                        },
                        (error) => {
                            const data = {
                                message: this.$t(error.message),
                                variant: 'danger',
                                title: this.$t('nedbrowser.fileTypeError'),
                                autoHideDelay: 10000
                            };
                            this.showToast(data);
                            this.isSaving = false;
                        }
                    );
                } else {
                    this.fileName = 'Geen bestand gekozen';
                }
            },
            /**
             * Show the import file selector
             */
            $_selectFile() {
                // call click on the hidden file selector
                this.$refs.fileSelector.click();
            },

            /**
             * Read the zip content
             * @param  {Array} files   [selected zip files]
             * @return {object}        [Promise object]
             */
            readFileContent(files) {
                return new Promise((resolve, reject) => {
                    const file = files[0];
                    if (file && file.size > 20971520) {
                        reject(new Error('Bestand is te groot'));
                        return;
                    }

                    const reader = new FileReader();
                    reader.onload = (e) => {
                        try {
                            const json = JSON.parse(e.target.result);
                            resolve(json);
                        } catch {
                            reject(new Error('Ongeldig JSON-bestand'));
                        }
                    };
                    reader.onerror = () => {
                        reject(new Error('Fout bij het lezen van het bestand'));
                    };

                    reader.readAsText(file);
                });
            },

            /**
             * Check selected zip file
             * @return {[type]} [description]
             */
            async $_onSubmit() {
                try {
                    this.isSaving = true;

                    if (!this.importData) {
                        return;
                    }

                    nprogress.start();
                    // import selected zip file
                    await this.$store.dispatch('useApiData', {
                        name: 'ProductImport',
                        data: {
                            productName: this.importData.productName,
                            productVersion: this.importData.productVersion,
                            productContent: JSON.stringify(
                                this.importData.productContent
                            )
                        }
                    });
                    const data = {
                        message: this.$t('product.importSuccess'),
                        variant: 'success',
                        autoHideDelay: 3000
                    };
                    this.showToast(data);
                    this.$emit('created');
                    await this.$_hideModal();
                } catch (error) {
                    this.showToast({
                        message: this.$t(error.data.response.data.message),
                        variant: 'danger',
                        autoHideDelay: 5000,
                        noCloseButton: true
                    });
                } finally {
                    nprogress.done();
                    this.isSaving = false;
                }
            }
        },
        watch: {
            launchModal() {
                this.fileName = 'Geen bestand gekozen';
                this.importData = null;
                this.isSaving = false;
                this.$_showModal();
            }
        }
    };
</script>

<style lang="scss" scoped>
.import-group {
    display: block;
    margin: 0px;
    padding: 0px;
    width: 100%;
    height: calc(1.5em + 0.75rem + 2px);
}

.import-control {
    background: #fff;
    cursor: pointer;
    border-radius: 0.25rem;
}

.import-icon {
    color: #007ac9;
    margin-left: 10px;
}

.import-title {
    margin-left: 10px;
    margin-right: 10px;
}

.import-name {
    margin-left: 10px;
    margin-right: 15px;
}

.import-error {
    color: red !important;
}
</style>
